import {
  useEffect, useMemo, useState, type CSSProperties, type ReactNode,
} from "react";
import { useT, type Uebersetzer } from "../i18n";
import { Punkt, euro } from "./index";
import { tagMitWochentag } from "./preistext";
import { alsTag, zweistellig } from "../daten/kalender";
import type { Typenlage } from "../daten/nachschlagen";
import type { Buendelpreis } from "../daten/preisbuendel";
import { useTelefon } from "../schale/geraet";
import { heuteImBetrieb } from "../daten/betriebstag";
import { vormerkTeile, vormerkWort } from "../daten/reserviertsatz";

/* ══════════════════════════════════════════════════════════════════
   Eine Zeile, die beides beantwortet — Lage und Preis.

   docs/38-vermietmodul.md: *„**Eine Zeile beantwortet beide Fragen.**
   Preis *und* Lage stehen nebeneinander, weil sie im Gespräch zusammen
   fallen: „Ist der Rüttler nächste Woche frei, und was kostet er?"
   Heute sind das zwei Blätter, zwischen denen man wechselt."*

   **Warum sie hier steht und nicht in der Maske.** Drei Sichten stellen
   dieselbe Frage — die Vermietfläche, der Verfügbarkeitscheck und die
   Preisauskunft —, und sie müssen dieselbe Antwort gleich aussehen
   lassen. Ein „ab Do 24.9. frei" in der einen und ein „belegt, wieder
   frei am 24.09." in der anderen sind zwei Vokabeln für denselben
   Befund; wer beide bedient, lernt sie beide.

   **Die Wörter sind die des Bretts** (`lage.*`) und keine neuen. Das war
   schon im Verfügbarkeitscheck so entschieden und steht dort
   ausgeschrieben; hier stehen sie jetzt einmal statt zweimal.

   Klassen: `tz-*`, gegen alle Stile geprüft.
   ══════════════════════════════════════════════════════════════════ */

/* ══════════════ Der Tagesstreifen ══════════════

   Luca, 17.09.2026: *„Auf dem Verfügbarkeitscheck sah die alte Version
   besser aus. Da war nämlich direkt auch die Woche dann sichtbar, sodass
   man das irgendwie cooler sieht."*

   Er hat recht, und der Grund ist nicht Geschmack: „ab Do 24.9. frei"
   sagt dasselbe wie der Streifen und **zeigt** es nicht. Wer am Telefon
   sucht, liest sieben Kästchen in einer Sekunde und den Satz in dreien.

   **Der Streifen ist nicht neu gebaut, sondern herausgezogen** — aus
   `masken/Schnellerfassung.tsx` und `stile/schnell.css`, wo er über drei
   Runden entstanden ist (Luca, 15.09.2026: *„Die einzelnen Tage sind
   mitgepackt, und die sind dann nicht genau in der Spalte von dem
   Tag."*). Die drei Vorkehrungen dagegen stehen ausgeschrieben im
   Stilblock und gelten hier unverändert:

     1. **Eine Rechnung.** `--tag-breite` und `--tage` setzt der Aufrufer,
        Kopf und Zeile lesen dieselben Variablen.
     2. **Ganze Pixel**, abgerundet statt `1fr` verteilt.
     3. **Dieselbe Innenbreite.** Der Kopf steht IN der rollenden Liste,
        und weder er noch die Zeile trägt einen Rahmen.

   Die Klassennamen bleiben die des Streifens (`se-tagekopf`, `se-band`,
   `se-tag`) — es ist derselbe Streifen, und `stile/schnell.css` ist über
   `main.tsx` ohnehin überall geladen. Sie umzubenennen hieße, die alte
   Maske anzufassen, und am Ende stünde dasselbe da.

   **Was die Lagen bedeuten, rechnet die andere Seite** (`tage` aus
   `GET /nachschlagen`) — aus demselben `belegt`, aus dem auch „ab wann
   wieder frei" entsteht. Hier wird gezeichnet, nicht gezählt. */

/** Die Maße der Zeile — sie stehen hier und gehen als Variablen an das CSS. */
const TR_PAD = 12;      // Innenrand der Zeile, links wie rechts
const TR_LUECKE = 12;   // zwischen den Spalten
const TR_NAME = 196;    // Typ und Gruppe — die schmalste noch lesbare Breite
const TR_LAGE = 132;    // die Lage
const TR_TAGE = 86;     // die Miettage
const TR_BETRAG = 96;   // der Preis
const TR_TAT = 104;     // was rechts getan wird — „Merken", oder nichts
const TAG_MIN = 15;     // schmaler wird eine Tagesspalte nicht lesbar
/* Und breiter braucht sie auch nicht zu werden: In einer Spalte steht ein
   Wochentag und eine zweistellige Zahl. */
const TAG_MAX = 34;
/* **Am Telefon ist die Zeile eine Karte** (17.09.2026): Name oben, der
   Streifen darunter über die ganze Breite, Lage und Preis in der Zeile
   danach, „Merken" groß am Fuß. Neben dem Streifen steht dort nichts —
   also rechnet er mit der Innenbreite der Karte, und eine Tagesspalte
   darf breiter werden als am Schreibtisch, weil ein Daumen sie liest. */
const TAG_MAX_TELEFON = 44;
/** Rand und Innenrand der Karte links und rechts — `stile/vermieten.css`. */
const KARTE_RAND = 2 * (TR_PAD + 1);

/**
 * Über wie viele Tage der Streifen höchstens geht.
 *
 * **31, also etwa ein Monat.** Darüber tritt er ganz zurück, statt kleiner
 * zu werden: Die Breitenrechnung würde ihn zwar ohnehin abschalten, sobald
 * eine Spalte unter `TAG_MIN` fällt — aber die Grenze hinge dann an der
 * Fensterbreite, und dieselbe Frage sähe auf einem großen Schirm anders aus
 * als auf einem kleinen. Wer über einen Monat fragt, fragt nicht mehr „an
 * welchem Tag", sondern „überhaupt"; dafür steht die Zeile daneben.
 */
const STREIFEN_HOECHSTENS = 31;

/** Ein Tag im Fenster — Beschriftung und Zusammenhang, keine Belegung. */
export type Tagesspalte = {
  iso: string; wt: string; zahl: string; wochenende: boolean; heute: boolean;
  /** Der Name eines Feiertags an diesem Tag (0287) — nur Anzeige, er zählt als Miettag. */
  feiertag?: string | null;
};

/** Die Tage eines Fensters, mit Beschriftung. Höchstens so viele wie der Streifen trägt. */
export function tagesspalten(
  ab: string,
  anzahl: number,
  wochentage: string[],
  /** Der Feiertag eines Tages (`useFeiertagAm`) — ohne ihn kennt der Streifen keinen. */
  feiertagAm?: (iso: string) => string | null,
): Tagesspalte[] {
  const raus: Tagesspalte[] = [];
  const heute = heuteImBetrieb();
  const [j, m, t] = ab.split("-").map(Number);
  const lauf = new Date(j ?? 1970, (m ?? 1) - 1, t ?? 1);
  for (let i = 0; i < Math.min(anzahl, STREIFEN_HOECHSTENS); i += 1) {
    const iso = alsTag(lauf);
    const wochentag = (lauf.getDay() + 6) % 7;
    raus.push({
      iso,
      wt: wochentage[wochentag] ?? "",
      zahl: zweistellig(lauf.getDate()),
      wochenende: wochentag >= 5,
      heute: iso === heute,
      feiertag: feiertagAm?.(iso) ?? null,
    });
    lauf.setDate(lauf.getDate() + 1);
  }
  return raus;
}

/**
 * Wie breit eine Tagesspalte sein darf — gemessen am **Fenster der Liste**.
 *
 * Nicht am Streifen: Der ist so breit, wie diese Rechnung ihn macht, und ihn
 * zu messen hieße, das eigene Ergebnis zu lesen. Abgerundet, damit Kopf und
 * Zeile dieselbe ganze Zahl haben. `0` heißt: Es passt nicht, also kein
 * Streifen — die Zeile sagt es dann in Worten.
 */
export function useTagesbreite(
  tage: number,
  ohnePreis = false,
): { ref: (el: HTMLElement | null) => void; breite: number; stil: CSSProperties } {
  /* **Ein Rückruf statt eines `useRef`.** Das Fenster der Liste steht erst da,
     wenn die Antwort da ist — davor zeichnet die Maske einen Ladebalken. Ein
     Effekt mit `[ref]` läuft genau einmal, findet `null` und meldet sich nie
     wieder an; der Streifen blieb damit für immer aus. Der Rückruf setzt einen
     Zustand, und der Effekt läuft, sobald der Knoten wirklich hängt. */
  const [knoten, setzeKnoten] = useState<HTMLElement | null>(null);
  const [fensterbreite, setzeFensterbreite] = useState(0);
  const telefon = useTelefon();
  useEffect(() => {
    if (!knoten) { setzeFensterbreite(0); return undefined; }
    const auge = new ResizeObserver((eintraege) => {
      const breite = eintraege[0]?.contentRect.width;
      if (breite !== undefined) setzeFensterbreite(Math.round(breite));
    });
    auge.observe(knoten);
    // Einmal sofort: Der Beobachter meldet sich zwar auch beim Anmelden, aber
    // erst im nächsten Bild — und bis dahin stünde die Liste ohne Streifen da.
    setzeFensterbreite(Math.round(knoten.getBoundingClientRect().width));
    return () => auge.disconnect();
  }, [knoten]);

  return useMemo(() => {
    const spalten = Math.min(tage, STREIFEN_HOECHSTENS);
    let breite = 0;
    if (telefon && fensterbreite && spalten > 0 && tage <= STREIFEN_HOECHSTENS) {
      const gerechnet = Math.min(TAG_MAX_TELEFON, Math.floor((fensterbreite - KARTE_RAND) / spalten));
      breite = gerechnet >= TAG_MIN ? gerechnet : 0;
    } else if (fensterbreite && spalten > 0 && tage <= STREIFEN_HOECHSTENS) {
      const inhalt = fensterbreite - TR_PAD;
      /* Was die festen Spalten und die Lücken lassen. **Ohne Preis sind es
         zwei Spalten und zwei Lücken weniger** — der Verfügbarkeitscheck
         steht in einem Blatt von 880 px und bekäme sonst keinen Streifen. */
      const feste = ohnePreis
        ? TR_NAME + TR_LAGE + TR_TAT + 3 * TR_LUECKE
        : TR_NAME + TR_LAGE + TR_TAGE + TR_BETRAG + TR_TAT + 5 * TR_LUECKE;
      const gerechnet = Math.min(TAG_MAX, Math.floor((inhalt - feste) / spalten));
      breite = gerechnet >= TAG_MIN ? gerechnet : 0;
    }
    return {
      ref: setzeKnoten,
      breite,
      stil: {
        "--tage": spalten,
        "--tag-breite": `${breite}px`,
        "--tr-pad": `${TR_PAD}px`,
        "--tr-luecke": `${TR_LUECKE}px`,
        "--tr-name": `${TR_NAME}px`,
        "--tr-lage": `${TR_LAGE}px`,
        "--tr-tage": `${TR_TAGE}px`,
        "--tr-betrag": `${TR_BETRAG}px`,
        "--tr-tat": `${TR_TAT}px`,
      } as CSSProperties,
    };
  }, [fensterbreite, tage, ohnePreis, telefon]);
}

/**
 * Der Tageskopf — er steht **in** der rollenden Liste und bleibt beim Rollen
 * oben stehen.
 *
 * Ein Streifen ohne Kopf sind nur Kästchen. Und er steht in der Liste und
 * nicht darüber, weil er nur dort dieselbe Innenbreite hat wie die Zeilen —
 * darüber wäre er den Rollbalken breiter, und genau so entsteht ein Tag, der
 * neben seiner Spalte steht.
 */
export function Tagekopf({ tage, ohnePreis = false }: {
  tage: Tagesspalte[];
  /** Ohne Preisspalten — dann sind es zwei leere Zellen weniger. */
  ohnePreis?: boolean;
}) {
  const t = useT();
  if (!tage.length) return null;
  return (
    <div className="se-tagekopf tz-tagekopf">
      <span className="se-tagekopf-schild">{t("schnell.tagekopf")}</span>
      <span className="se-band">
        {tage.map((tg) => (
          <span
            className={`se-tagkopf${tg.wochenende ? " we" : ""}${tg.feiertag ? " ft" : ""}${tg.heute ? " heute" : ""}`}
            key={tg.iso}
            {...(tg.feiertag ? { title: t("feiertage.spalte", { name: tg.feiertag }) } : {})}
          >
            <b>{tg.wt}</b>
            <i>{tg.zahl}</i>
          </span>
        ))}
      </span>
      {/* Leere Zellen für Lage, Miettage, Betrag und was rechts getan wird.
          Sie tragen nichts und halten trotzdem die Spalten — ohne sie
          rutschte der Streifen im Kopf gegen den in der Zeile. Genau so
          viele, wie die Zeile darunter hat. */}
      <span />
      {ohnePreis ? null : <><span /><span /></>}
      <span />
    </div>
  );
}

/** Welcher Punkt neben der Zeile steht — dieselben drei wie im Brett. */
export const punktVon = (lage: string): "gut" | "acht" | "stopp" =>
  lage === "geht" ? "gut" : lage === "teils" ? "acht" : "stopp";

/**
 * Das Wort zur Lage — frei, teils, belegt.
 *
 * Es sind die Wörter des Bretts (`lage.*`) und nicht neue: Wer im Brett
 * „Teilweise belegt" liest und hier „teils", hat zwei Vokabeln für denselben
 * Befund gelernt.
 */
export function lageWort(t: Uebersetzer, z: Typenlage): string {
  if (z.bestand === 0) return t("lage.kein_geraet");
  // Frei wäre es — ein Angebot hält es. Das eigene Wort dafür, seit dem
  // 18.09.2026: „teilweise" hieße, ein Teil des Zeitraums sei belegt.
  // Angebot, Auftrag im Abschluss oder „wird gerade vermietet" (N7-05).
  const vorgemerkt = vormerkWort(z);
  if (vorgemerkt) return t(vorgemerkt);
  if (z.lage === "geht") return t("lage.frei");
  if (z.lage === "teils") return t("lage.teilweise");
  return t("lage.belegt");
}

/**
 * Die zweite Auskunft — nur dort, wo die erste nein heißt.
 *
 * „Belegt" beendet das Gespräch, „belegt, ab Montag wieder frei" führt es
 * weiter. Der Tag gilt der **gefragten Dauer**: Ein einzelner freier
 * Donnerstag zwischen zwei Aufträgen ist keine Zusage (`dienste/nachschlagen.ts`).
 */
export function wiederFreiSatz(t: Uebersetzer, z: Typenlage): string {
  /* **Kein Gerät ist nicht dasselbe wie kein freies Gerät** (18.09.2026).
     Bei `bestand === 0` stand hier „In den nächsten vier Monaten nichts am
     Stück frei" — ein Satz über Belegungen an einem Typ, von dem der Betrieb
     kein Stück hat. Das Wort daneben (`lageWort`) sagt es richtig; hier ist
     dann nichts zu ergänzen. */
  if (z.bestand === 0 || z.frei > 0) return "";
  return z.freiAb
    ? t("nachschlagen.wiederFrei", { tag: tagMitWochentag(t, z.freiAb) })
    : t("nachschlagen.langeNichts");
}

/**
 * Was der Preis dieser Zeile sagt — oder warum er nichts sagt.
 *
 * **Ein Typ ohne Tarif bekommt keine Null.** Eine Null sähe aus wie ein
 * Preis, und ein Preis von 0,00 am Telefon ist eine Zusage. Wo im Stamm
 * nichts steht, steht hier ein Wort.
 */
export function preistext(t: Uebersetzer, preis: Buendelpreis | undefined): {
  betrag: string;
  still: boolean;
} {
  if (!preis) return { betrag: t("allgemein.laedt"), still: true };
  if (preis.ohnePreis === "nicht_gefunden") return { betrag: t("allgemein.strich"), still: true };
  if (preis.ohnePreis === "kein_tarif") return { betrag: t("vermieten.keinTarif"), still: true };
  return { betrag: euro(preis.netto), still: false };
}

/**
 * Die Zeile selbst.
 *
 * Sie zeichnet nur — was ein Klick bedeutet, entscheidet die Maske darum
 * herum: In der Vermietfläche wählt er den Posten aus, im
 * Verfügbarkeitscheck klappt er die Stücke auf. Eine Zeile, die selbst
 * wüsste, was sie tut, ließe sich nur in einer der beiden benutzen.
 */
export function Trefferzeile({
  name, wo, lage, preis, gewaehlt, waehlen, tage, ohnePreis = false, rechts, kinder, frisch = false,
}: {
  name: string;
  /** Gruppe und Übergruppe, wenn sie etwas unterscheiden. */
  wo: string;
  lage: Typenlage;
  /** Der Preis im gefragten Zeitraum. Fehlt er, steht „lädt" an seiner Stelle. */
  preis: Buendelpreis | undefined;
  gewaehlt: boolean;
  waehlen: () => void;
  /**
   * Die Tage des Streifens. Leer heißt: kein Streifen — dann steht die
   * Auskunft nur in Worten, und das ist in Ordnung. Sie kommt vom Aufrufer,
   * weil er die Breite gemessen hat und weiß, ob sie passt.
   */
  tage?: Tagesspalte[];
  /**
   * Ohne Preisspalten — der Verfügbarkeitscheck fragt nach dem Gerät und
   * nicht nach dem Preis (Luca, 17.09.2026). Eine leere Spalte mit „lädt"
   * darin wäre eine Auskunft über etwas, das niemand gefragt hat.
   */
  ohnePreis?: boolean;
  /** Was ganz rechts steht — in der Vermietfläche der Griff „Merken". */
  rechts?: ReactNode;
  /** Was unter der Zeile aufgeht, wenn sie gewählt ist. */
  kinder?: ReactNode;
  /**
   * **Der Betrag gilt noch der vorigen Frage** (19.09.2026). Im Nachschlagen
   * am Telefon lässt sich je Typ die Ausstattung oder der Bediener ändern;
   * bis die neue Zahl da ist, wird die Zeile leise statt „lädt" zu zeigen —
   * ein Wort an der Stelle des Betrags ließe die Liste springen.
   */
  frisch?: boolean;
}) {
  const t = useT();
  const punkt = punktVon(lage.bestand === 0 ? "nein" : lage.lage);
  const wieder = wiederFreiSatz(t, lage);
  const zahl = preistext(t, preis);
  const streifen = tage ?? [];

  return (
    <article
      className={`tz ${punkt}${gewaehlt ? " markiert" : ""}${frisch ? " wird-frisch" : ""}`}
      {...(frisch ? { "aria-busy": true } : {})}
    >
      <button
        type="button"
        className="tz-kopf"
        aria-expanded={gewaehlt}
        aria-pressed={gewaehlt}
        onClick={waehlen}
      >
        <span className="tz-was">
          <b className="tz-name">{name}</b>
          {wo ? <span className="tz-wo">{wo}</span> : null}
        </span>

        {/* **Die Woche, sichtbar.** Je Tag ein Kästchen, ausgerichtet am
            Kopf über der Liste. Was es bedeutet, steht im Stilblock; was an
            welchem Tag los ist, hat die andere Seite gerechnet. */}
        {streifen.length ? (
          <span className="se-band tz-band" aria-hidden="true">
            {streifen.map((tg, i) => (
              <span
                className={`se-tag ${lage.tage[i] ?? "frei"}${tg.wochenende ? " we" : ""}${tg.feiertag ? " ft" : ""}${tg.heute ? " heute" : ""}`}
                key={tg.iso}
              />
            ))}
          </span>
        ) : null}

        <span className="tz-lage">
          <Punkt art={punkt} />
          <span className="tz-lage-text">
            <b>{lageWort(t, lage)}</b>
            {/* Wie viele Stücke von wie vielen — die Zahl ist die Auskunft.
                Die Werkstattgeräte zählen zum Bestand, aber nie zur
                Verfügbarkeit: Ein Gerät in Reparatur ist nicht belegt, es
                ist nicht da. */}
            <span>
              {/* „0 von 0 frei" ist keine Auskunft, sondern eine Rechnung
                  über nichts: Wo es kein Stück gibt, sagt das Wort darüber
                  alles (18.09.2026). */}
              {lage.bestand === 0
                ? ""
                : wieder || t("nachschlagen.freiVon", { frei: lage.frei, bestand: lage.bestand })}
              {/* Was ein Angebot hält, steht daneben — nicht in der Zahl der
                  freien Stücke (18.09.2026). Wer es trotzdem nimmt, bestätigt
                  das im Blatt zum Posten. */}
              {vormerkTeile(lage).map((teil) => ` · ${t(teil.schluessel, { zahl: teil.zahl })}`).join("")}
            </span>
          </span>
        </span>

        {ohnePreis ? null : (
          <>
            <span className="tz-tage">
              {preis && !preis.ohnePreis
                ? (preis.miettage === 1
                  ? t("preisauskunft.einMiettag")
                  : t("preisauskunft.miettage", { zahl: preis.miettage }))
                : ""}
            </span>
            <b className={`tz-betrag${zahl.still ? " still" : ""}`}>{zahl.betrag}</b>
          </>
        )}
      </button>

      <div className="tz-rechts">{rechts}</div>
      {gewaehlt && kinder ? <div className="tz-auf">{kinder}</div> : null}
    </article>
  );
}
